/**
 * FieldMarkers - Stateless SVG component for rendering field lines
 * Used by FootballField (editor) and play renderers (thumbnails)
 *
 * Supports two modes:
 * 1. Legacy mode: width, height, transformPoint (pixel-based)
 * 2. Config mode: config prop (viewBox-based, for PlayRenderer)
 */

import type { Coordinate } from '@/types'
import type { FieldConfiguration } from '@/types/Field.types'
import {
	FIELD_WIDTH_FEET,
	HASH_MARK_OPACITY,
	HASH_MARK_STROKE_WIDTH,
	HASH_SPACING,
	HASH_WIDTH,
	LEFT_HASH_CENTER,
	LINE_OF_SCRIMMAGE,
	LOS_STROKE_WIDTH,
	NUMBER_FROM_EDGE,
	NUMBER_HEIGHT,
	RIGHT_HASH_CENTER,
	YARD_LINE_STROKE_WIDTH,
} from '@/constants'
import '../../styles/field-colors.css'
import React from 'react'

type FieldMarkersProps = {
	// Legacy mode props (pixel-based)
	width?: number
	height?: number
	transformPoint?: (point: Coordinate) => Coordinate
	minY?: number
	maxY?: number

	// Config mode props (viewBox-based)
	config?: FieldConfiguration
	lineOfScrimmage?: number
}

export const FieldMarkers = React.memo(function FieldMarkers({
	width,
	height,
	transformPoint,
	minY = 0,
	maxY,
	config,
	lineOfScrimmage,
}: FieldMarkersProps) {
	// Determine mode and validate props
	const isConfigMode = !!config
	const isLegacyMode = !!(width && height && transformPoint)

	if (!isConfigMode && !isLegacyMode) {
		console.error('FieldMarkers: Must provide either config or (width, height, transformPoint)')
		return null
	}

	// Config mode: viewBox-based rendering
	if (isConfigMode) {
		return <FieldMarkersConfigMode config={config} lineOfScrimmage={lineOfScrimmage} />
	}

	// Legacy mode: pixel-based rendering
	return (
		<FieldMarkersLegacyMode
			width={width!}
			height={height!}
			transformPoint={transformPoint!}
			minY={minY}
			maxY={maxY}
		/>
	)
})

/**
 * Config mode: ViewBox-based rendering for PlayRenderer
 */
function FieldMarkersConfigMode({
	config,
	lineOfScrimmage,
}: {
	config: FieldConfiguration
	lineOfScrimmage?: number
}) {
	const { hashMarks, overlays, style } = config

	// Use config values with fallbacks
	const leftHashX = hashMarks?.leftHashX ?? -18.5
	const rightHashX = hashMarks?.rightHashX ?? 18.5
	const showHashMarks = style?.showHashMarks ?? true
	const showYardNumbers = style?.showYardNumbers ?? true

	return (
		<g className="field-markers">
			{/* Yard lines and hash marks - simplified for viewBox mode */}
			{/* TODO: Implement full field rendering in viewBox coordinates */}

			{/* Red line overlay */}
			{overlays?.redLine && (
				<line
					x1={-80 + (overlays.redLineOffset ?? 5)}
					y1={0}
					x2={-80 + (overlays.redLineOffset ?? 5)}
					y2={120}
					stroke="red"
					strokeWidth={0.2}
					opacity={0.5}
				/>
			)}

			{/* Line of scrimmage */}
			{lineOfScrimmage !== undefined && (
				<line
					x1={-80}
					y1={lineOfScrimmage}
					x2={80}
					y2={lineOfScrimmage}
					stroke="var(--field-los)"
					strokeWidth={0.5}
				/>
			)}
		</g>
	)
}

/**
 * Legacy mode: Pixel-based rendering for FootballField
 */
function FieldMarkersLegacyMode({
	width,
	height,
	transformPoint,
	minY = 0,
	maxY,
}: {
	width: number
	height: number
	transformPoint: (point: Coordinate) => Coordinate
	minY?: number
	maxY?: number
}) {
	const elements: React.ReactNode[] = []

	// Calculate how many yards to show
	const actualMaxY = maxY ?? Math.ceil((height * FIELD_WIDTH_FEET) / width / HASH_SPACING) * HASH_SPACING + 10

	// Pre-calculate values that don't change in the loop
	const scale = width / FIELD_WIDTH_FEET
	const fontSize = NUMBER_HEIGHT * scale
	const numberCenterX = NUMBER_FROM_EDGE + NUMBER_HEIGHT / 2
	const numberLeftX = transformPoint({ x: numberCenterX, y: 0 }).x
	const numberRightX = transformPoint({ x: FIELD_WIDTH_FEET - numberCenterX, y: 0 }).x

	// Pre-calculate hash mark x-coordinates (same for all hash marks)
	const leftHashStart = transformPoint({ x: LEFT_HASH_CENTER - HASH_WIDTH / 2, y: 0 }).x
	const leftHashEnd = transformPoint({ x: LEFT_HASH_CENTER + HASH_WIDTH / 2, y: 0 }).x
	const rightHashStart = transformPoint({ x: RIGHT_HASH_CENTER - HASH_WIDTH / 2, y: 0 }).x
	const rightHashEnd = transformPoint({ x: RIGHT_HASH_CENTER + HASH_WIDTH / 2, y: 0 }).x

	for (let yFeet = minY; yFeet <= actualMaxY; yFeet += HASH_SPACING) {
		const isYardLine = yFeet % (HASH_SPACING * 5) === 0
		const isLOS = yFeet === LINE_OF_SCRIMMAGE

		const strokeWidth = isYardLine ? (isLOS ? LOS_STROKE_WIDTH : YARD_LINE_STROKE_WIDTH) : HASH_MARK_STROKE_WIDTH
		const y = transformPoint({ x: 0, y: yFeet }).y

		// Skip lines too close to container edges (would clip visually)
		if (y < strokeWidth || y > height - strokeWidth) {
			continue
		}

		if (isYardLine) {
			// Full yard line
			elements.push(
				<line
					key={`yard-${yFeet}`}
					x1={0}
					y1={y}
					x2={width}
					y2={y}
					stroke={isLOS ? 'var(--field-los)' : 'var(--field-line)'}
					strokeWidth={strokeWidth}
					opacity={isLOS ? 'var(--field-los-opacity)' : 'var(--field-line-opacity)'}
				/>,
			)

			// Number labels at 10-yard intervals
			if (yFeet % 10 === 0 && yFeet > 0) {
				elements.push(
					<text
						key={`label-left-${yFeet}`}
						x={numberLeftX}
						y={y}
						fontSize={fontSize}
						fill="var(--field-text)"
						textAnchor="middle"
						dominantBaseline="middle"
						transform={`rotate(-90 ${numberLeftX} ${y})`}
						opacity="var(--field-text-opacity)"
					>
						# #
					</text>,
				)

				elements.push(
					<text
						key={`label-right-${yFeet}`}
						x={numberRightX}
						y={y}
						fontSize={fontSize}
						fill="var(--field-text)"
						textAnchor="middle"
						dominantBaseline="middle"
						transform={`rotate(90 ${numberRightX} ${y})`}
						opacity="var(--field-text-opacity)"
					>
						# #
					</text>,
				)
			}
		} else {
			// Hash marks only
			elements.push(
				<line
					key={`left-hash-${yFeet}`}
					x1={leftHashStart}
					y1={y}
					x2={leftHashEnd}
					y2={y}
					stroke="var(--field-line)"
					strokeWidth={HASH_MARK_STROKE_WIDTH}
					opacity={HASH_MARK_OPACITY}
				/>,
			)

			elements.push(
				<line
					key={`right-hash-${yFeet}`}
					x1={rightHashStart}
					y1={y}
					x2={rightHashEnd}
					y2={y}
					stroke="var(--field-line)"
					strokeWidth={HASH_MARK_STROKE_WIDTH}
					opacity={HASH_MARK_OPACITY}
				/>,
			)
		}
	}

	return <>{elements}</>
}
